"use client";

import { useState } from "react";
import { BarChart3, Maximize2, ExternalLink, Download } from "lucide-react";
import type { RichChart } from "@/lib/caonabo/rich-content";

type Props = { chart: RichChart };

const TERRA_LIGHT = "#D77F70";
const PALETTE = ["#8B3A2E", "#D77F70", "#C9826F", "#A85A4C", "#E8A593", "#6B2820", "#F2C4B6"];

/**
 * Renderiza una gráfica usando QuickChart.io (servicio público gratuito).
 * Devuelve URL de imagen PNG generada server-side. Click expande,
 * download descarga PNG.
 */
export default function ChatChartCard({ chart }: Props) {
  const [expanded, setExpanded] = useState(false);

  // Asignar colores de la paleta terracotta si no vienen
  const datasets = chart.datasets.map((d, i) => ({
    ...d,
    backgroundColor:
      d.backgroundColor ??
      (chart.chartType === "pie" || chart.chartType === "doughnut"
        ? d.data.map((_, j) => PALETTE[j % PALETTE.length])
        : PALETTE[i % PALETTE.length]),
    borderColor:
      chart.chartType === "line" ? PALETTE[i % PALETTE.length] : undefined,
    fill: chart.chartType === "line" ? false : undefined,
  }));

  // Mapeo de tipo a Chart.js v4
  const chartJsType =
    chart.chartType === "horizontalBar"
      ? "bar"
      : chart.chartType;

  const chartConfig = {
    type: chartJsType,
    data: { labels: chart.labels, datasets },
    options: {
      indexAxis: chart.chartType === "horizontalBar" ? "y" : "x",
      plugins: {
        legend: {
          display: datasets.length > 1 || chart.chartType === "pie" || chart.chartType === "doughnut",
          labels: { color: "#888" },
        },
        title: {
          display: false, // ya tenemos header propio
        },
      },
      scales:
        chart.chartType === "pie" ||
        chart.chartType === "doughnut" ||
        chart.chartType === "radar"
          ? undefined
          : {
              x: { ticks: { color: "#aaa" }, grid: { color: "rgba(255,255,255,0.05)" } },
              y: { ticks: { color: "#aaa" }, grid: { color: "rgba(255,255,255,0.05)" } },
            },
    },
  };

  const w = expanded ? 1000 : 500;
  const h = expanded ? 600 : 320;
  const url = `https://quickchart.io/chart?c=${encodeURIComponent(JSON.stringify(chartConfig))}&w=${w}&h=${h}&backgroundColor=transparent&format=png&v=4`;
  const downloadUrl = url + "&download=1";

  return (
    <div
      className="my-2 rounded-2xl border overflow-hidden caonabo-fade-in"
      style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(139,58,46,0.25)" }}
    >
      <div
        className="px-3 py-2 flex items-center justify-between gap-2"
        style={{
          background: "linear-gradient(to right, rgba(139,58,46,0.12), rgba(139,58,46,0.06))",
          borderBottom: "1px solid rgba(139,58,46,0.20)",
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <BarChart3 size={14} style={{ color: TERRA_LIGHT }} className="shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-bold truncate" style={{ color: TERRA_LIGHT }}>
              {chart.title}
            </span>
            {chart.subtitle && (
              <span className="text-[10px] text-text-muted truncate">{chart.subtitle}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-lg transition-colors text-text-muted hover:text-[#D77F70]"
            title="Descargar PNG"
          >
            <Download size={12} />
          </a>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-lg transition-colors text-text-muted hover:text-[#D77F70]"
            title="Abrir en nueva pestaña"
          >
            <ExternalLink size={12} />
          </a>
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            className="p-1.5 rounded-lg transition-colors text-text-muted hover:text-[#D77F70]"
            title={expanded ? "Vista normal" : "Vista expandida"}
          >
            <Maximize2 size={12} />
          </button>
        </div>
      </div>
      <div className="bg-black/10">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={url}
          alt={chart.title}
          className="w-full h-auto block"
          style={{ maxHeight: expanded ? "600px" : "320px", objectFit: "contain" }}
        />
      </div>
    </div>
  );
}
